import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { AdminSession } from '../App';
import { useAdminLang } from '../App';
import { ADMIN_LABELS } from '../i18n';
import './DashboardLayout.scss';

interface Props {
  session: AdminSession;
  onLogout: () => void;
}

export default function DashboardLayout({ session, onLogout }: Props) {
  const navigate = useNavigate();
  const { lang, setLang } = useAdminLang();
  const t = ADMIN_LABELS[lang];

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  return (
    <div className={`dashboard-layout${lang === 'ar' ? ' rtl' : ''}`} dir={t.dir}>
      {/* Mobile top bar */}
      <header className="mobile-topbar">
        <div className="mobile-topbar-left">
          <img src="/logo.png" alt="Dubai Police" className="mobile-logo" />
          <div className="mobile-brand-text">
            <div className="mobile-brand-ar">شرطة دبي</div>
            <div className="mobile-brand-en">DUBAI POLICE</div>
          </div>
        </div>
        <div className="mobile-topbar-right">
          <div className="mobile-lang-toggle">
            <button
              className={`mobile-lang-btn${lang === 'en' ? ' active' : ''}`}
              onClick={() => setLang('en')}
            >En</button>
            <button
              className={`mobile-lang-btn${lang === 'ar' ? ' active' : ''}`}
              onClick={() => setLang('ar')}
            >Ar</button>
          </div>
        </div>
      </header>

      {/* Desktop icon strip sidebar */}
      <aside className="icon-strip">
        <div className="strip-logo">
          <img src="/logo.png" alt="Dubai Police" className="strip-logo-img" />
        </div>

        <nav className="strip-nav">
          <NavLink
            to="/conversations"
            className={({ isActive }) => `strip-nav-item${isActive ? ' active' : ''}`}
            title={t.navConversations}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </NavLink>
          <NavLink
            to="/knowledge-base"
            className={({ isActive }) => `strip-nav-item${isActive ? ' active' : ''}`}
            title={t.navKnowledgeBase}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
            </svg>
          </NavLink>
        </nav>

        <div className="strip-footer">
          {/* Language toggle: En | Ar pill */}
          <div className="strip-lang">
            <button
              className={`strip-lang-btn${lang === 'en' ? ' active' : ''}`}
              onClick={() => setLang('en')}
            >En</button>
            <button
              className={`strip-lang-btn${lang === 'ar' ? ' active' : ''}`}
              onClick={() => setLang('ar')}
            >Ar</button>
          </div>

          {/* Admin initial + logout */}
          <div className="strip-user-row">
            <div className="strip-avatar">
              {(session.name || 'A').charAt(0).toUpperCase()}
            </div>
            <button className="strip-logout-btn" onClick={handleLogout} title={t.logout}>
              {/* Power / logout icon */}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
        </div>
      </aside>

      <main className="main-content">
        <Outlet />
      </main>

      {/* Mobile bottom navigation */}
      <nav className="mobile-bottom-nav">
        <NavLink
          to="/conversations"
          className={({ isActive }) => `mobile-nav-item${isActive ? ' active' : ''}`}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <span className="mobile-nav-label">{t.navConversations}</span>
        </NavLink>
        <NavLink
          to="/knowledge-base"
          className={({ isActive }) => `mobile-nav-item${isActive ? ' active' : ''}`}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
          </svg>
          <span className="mobile-nav-label">{t.navKnowledgeBase}</span>
        </NavLink>
        <button className="mobile-nav-item mobile-nav-user" onClick={handleLogout}>
          <div className="mobile-nav-avatar">
            {(session.name || 'A').charAt(0).toUpperCase()}
          </div>
          <span className="mobile-nav-label">{session.name || 'Admin'}</span>
        </button>
      </nav>
    </div>
  );
}
